/* eslint-disable camelcase */
import { mapActions, mapState } from 'vuex';

/**
 * Mixin que encapsula la lógica de descarga dual de informes PDF:
 *  - Informe de estudio  → id: study_id,  type: 3  (solo si existe study_id)
 *  - Informe individual  → id: sample_id, type: 1  (siempre)
 *
 * Expone:
 *  · downloadDualReport({ sample_id, study_id, code })
 *      → devuelve `true` si al menos un PDF se descargó, `false` si ninguno.
 *  · triggerPdfDownload(base64, filename)
 *      → crea el <a> y dispara la descarga.
 *
 * El componente que lo usa es responsable de mostrar notificaciones
 * antes/después según su contexto (tabla o detalle).
 * Las acciones setReportSampleId y getReport se mapean aquí
 */
export const downloadReportMixin = {
  computed: {
    ...mapState('samples', ['allFilterParams']),
  },
  methods: {
    /**
     * Builds the report-download filters from the page currently displayed by
     * the samples grid. The grid uses zero-based page indexes, while the API
     * expects an offset and a limit.
     *
     * @returns {Object} filters for the current grid page
     */
    getSamplesPageDownloadParams() {
      const gridComponent = this.grid?.component;
      const pageIndex = Number(gridComponent?.pageIndex?.());
      const pageSize = Number(gridComponent?.pageSize?.());

      if (
        !Number.isFinite(pageIndex) ||
        pageIndex < 0 ||
        !Number.isFinite(pageSize) ||
        pageSize <= 0
      ) {
        return { ...this.allFilterParams };
      }

      return {
        ...this.allFilterParams,
        offset: pageIndex * pageSize,
        limit: pageSize,
      };
    },

    /**
     * Descarga un PDF codificado en base64.
     * @param {string} base64
     * @param {string} filename
     */
    triggerPdfDownload(base64, filename) {
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${base64}`;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    /**
     * Ejecuta la descarga dual (estudio + individual).
     * Gestiona internamente los errores por petición para que un fallo
     * en una no cancele la otra.
     *
     * @param {{ sample_id: string|number, study_id: string|number|null, code: string }} param
     * @returns {Promise<boolean>} true si al menos un informe fue descargado.
     */
    async downloadDualReport({ sample_id, study_id, code }) {
      const DOC_TYPE_GENERAL = 1;
      const DOC_TYPE_STUDY = 3;
      let downloaded = false;

      if (
        code !== undefined &&
        code !== null &&
        String(code).trim() !== ''
      ) {
        this.setReportSampleCode(code);
      }

      // Informe de estudio: study_id + type 3 (solo si existe study_id)
      if (study_id) {
        this.setReportSampleId(study_id);
        try {
          const reportStudy = await this.getReport(DOC_TYPE_STUDY);
          if (reportStudy.name && reportStudy.pdfB64) {
            this.triggerPdfDownload(
              reportStudy.pdfB64,
              `${code}_${DOC_TYPE_STUDY}.pdf`
            );
            downloaded = true;
          }
        } catch {
          // No hay informe de estudio, continuar
        }
      }

      // Guardamos el código antes de resolver el informe individual para que
      // `getReport` priorice `sample_code` frente al id interno de la fila.
      this.setReportSampleId(sample_id);
      try {
        const reportIndividual = await this.getReport(
          DOC_TYPE_GENERAL
        );
        if (reportIndividual.name && reportIndividual.pdfB64) {
          this.triggerPdfDownload(
            reportIndividual.pdfB64,
            `${code}_${DOC_TYPE_GENERAL}.pdf`
          );
          downloaded = true;
        }
      } catch {
        // No hay informe individual, continuar
      }

      return downloaded;
    },

    ...mapActions('samples', [
      'setReportSampleId',
      'setReportSampleCode',
      'getReport',
    ]),
  },
};

// Re-exportamos NOTIFY_TIME_LONG para los componentes que usen este mixin
export { NOTIFY_TIME_LONG } from '@/js/constants';
